import React, { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import AddBranch from "../components/AddBranch";
import EditBranch from "../components/EditBranch";
import { privateAxios } from "../utils/axios";

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

function Branch() {
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isDeletingCourier, setIsDeletingCourier] = useState(false);
  const limitOptions = [5, 10, 20, 50, 100];
  const searchInputRef = useRef(null);

  const queryClient = useQueryClient();

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const {
    data: branchesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["branches", currentPage, debouncedSearchTerm, limit, sourceFilter],
    queryFn: async () => {
      const response = await privateAxios.get(
        `/branches/?limit=${limit}&page=${currentPage}&q=${debouncedSearchTerm}&source=${sourceFilter}`
      );
      if (!response.data.success) {
        throw new Error("Network response was not ok");
      }
      return response.data;
    },
  });

  useEffect(() => {
    if (
      searchInputRef.current &&
      document.activeElement !== searchInputRef.current
    ) {
      searchInputRef.current.focus();
    }
  }, [branchesData]);

  const modalSetting = () => {
    setShowModal(!showModal);
  };

  const handleEdit = (branch) => {
    setSelectedBranch(branch);
    setShowEditModal(true);
  };

  const editModalSetting = () => {
    setShowEditModal(false);
    setSelectedBranch(null);
  };

  const handleSearchTerm = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleSourceFilterChange = (e) => {
    setSourceFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleSyncFromCourier = async () => {
    setIsSyncing(true);
    try {
      const response = await privateAxios.post("/branches/sync-courier");
      if (response.data.success) {
        const { created, updated, skipped, totalFetched } = response.data.data;
        toast.success(
          `Sync complete: ${created} created, ${updated} updated${
            skipped ? `, ${skipped} skipped` : ""
          } (of ${totalFetched} fetched)`
        );
        queryClient.invalidateQueries(["branches"]);
      } else {
        toast.error(response.data.message || "Failed to sync branches");
      }
    } catch (err) {
      console.error("Error syncing branches from courier:", err);
      toast.error(
        err?.response?.data?.message || "Error syncing branches from courier"
      );
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDeleteAllCourierImports = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to delete ALL courier-imported branches? This action cannot be undone."
    );
    if (!confirmed) return;

    setIsDeletingCourier(true);
    try {
      const response = await privateAxios.delete("/branches/courier-imports");
      if (response.data.success) {
        toast.success(
          `Deleted ${response.data.data?.deletedCount || 0} courier-imported branches`
        );
        queryClient.invalidateQueries(["branches"]);
      } else {
        toast.error(response.data.message || "Failed to delete courier imports");
      }
    } catch (err) {
      console.error("Error deleting courier imports:", err);
      toast.error(
        err?.response?.data?.message || "Error deleting courier imports"
      );
    } finally {
      setIsDeletingCourier(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [limit]);

  if (error) return <div>Error: {error.message}</div>;

  const deleteBranch = async (id) => {
    try {
      const response = await privateAxios.delete(`/branches/${id}`);

      if (response.data.success) {
        toast.success("Branch deleted successfully");
        queryClient.invalidateQueries(["branches"]);
      } else {
        toast.error(response.data.message || "Failed to delete branch");
      }
    } catch (err) {
      console.error("Error deleting branch:", err);
      toast.error(err?.response?.data?.message || "Error deleting branch");
    }
  };

  const handleDeleteBranchClick = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this branch? This action cannot be undone."
    );

    if (!confirmed) return;

    await deleteBranch(id);
  };

  return (
    <div className="col-span-12 lg:col-span-10 flex justify-center">
      <div className="flex flex-col gap-5 w-11/12">
        <div className="bg-white rounded-xl border border-gray-200 p-6 w-full mt-6 mb-8">
          <div>
            <span className="font-bold text-xl text-gray-900">
              Overall branches
            </span>
            <div className="text-gray-500 text-sm mt-1 mb-6">
              Overview of your branches
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 bg-gray-50 rounded-lg flex items-center gap-4 p-5">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100">
                <svg
                  className="w-6 h-6 text-blue-500"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M3 21h18M5 21V7l7-4 7 4v14M9 9h1m4 0h1m-6 4h1m4 0h1m-6 4h1m4 0h1"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                  />
                </svg>
              </div>
              <div>
                <div className="text-gray-500 text-sm">Total Branches</div>
                <div className="font-bold text-2xl text-gray-900">
                  {branchesData?.data?.branches?.length || 0}
                </div>
              </div>
            </div>
          </div>
        </div>

        {showModal && (
          <AddBranch
            addBranchModalSetting={modalSetting}
            handlePageUpdate={() => queryClient.invalidateQueries(["branches"])}
          />
        )}

        {showEditModal && selectedBranch && (
          <EditBranch
            branch={selectedBranch}
            editBranchModalSetting={editModalSetting}
            handlePageUpdate={() => queryClient.invalidateQueries(["branches"])}
          />
        )}

        <div className="overflow-x-auto rounded-lg border bg-white border-gray-200 mb-10">
          <div className="flex justify-between pt-5 pb-3 px-3 flex-col md:flex-row md:items-center my-3">
            <div className="flex gap-4 justify-center items-center w-full md:w-auto mb-4 md:mb-0">
              <span className="font-bold text-lg">Branches</span>
              <div className="relative flex items-center px-2 border-2 rounded-md shadow-sm w-80 bg-white">
                <img
                  alt="search-icon"
                  className="w-5 h-5"
                  src={require("../assets/search-icon.png")}
                />
                <input
                  ref={searchInputRef}
                  className="border-none outline-none focus:outline-none focus:ring-0 focus:border-none text-base pl-2 pr-6 bg-transparent w-full"
                  type="text"
                  placeholder="Search here"
                  value={searchTerm}
                  onChange={handleSearchTerm}
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="absolute right-2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    onClick={() => {
                      setSearchTerm("");
                      searchInputRef.current?.focus();
                    }}
                    tabIndex={-1}
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              <select
                className="px-3 py-2 border rounded-md text-sm bg-white"
                value={sourceFilter}
                onChange={handleSourceFilterChange}
              >
                <option value="">All Sources</option>
                <option value="manual">Manual</option>
                <option value="courier">Courier Import</option>
              </select>
            </div>
            <div className="flex gap-3 justify-end w-full md:w-auto flex-wrap">
              <button
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-5 py-2 text-base rounded-lg transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={handleSyncFromCourier}
                disabled={isSyncing}
              >
                {isSyncing ? "Syncing..." : "Sync from Courier"}
              </button>
              <button
                className="bg-red-600 hover:bg-red-700 text-white font-semibold px-5 py-2 text-base rounded-lg transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={handleDeleteAllCourierImports}
                disabled={isDeletingCourier}
              >
                {isDeletingCourier ? "Deleting..." : "Delete Courier Imports"}
              </button>
              <button
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-6 py-2 text-base rounded-lg transition-all duration-200 min-w-[160px]"
                onClick={modalSetting}
              >
                Add Branch
              </button>
            </div>
          </div>
          <table className="min-w-full divide-y divide-gray-200 text-sm overflow-hidden shadow-sm border">
            <thead>
              <tr className="bg-gray-200 border-b">
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[6%] text-sm">
                  S.No
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[16%] text-sm">
                  Name
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[12%] text-sm">
                  Number
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[15%] text-sm">
                  Receiver Name
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[12%] text-sm">
                  Receiver Number
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[20%] text-sm">
                  Address
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[9%] text-sm">
                  Branch Code
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[8%] text-sm">
                  Source
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[10%] text-sm">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="9" className="px-4 py-8 text-center">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                      <span className="ml-2 text-sm text-gray-500">
                        Loading branches...
                      </span>
                    </div>
                  </td>
                </tr>
              ) : branchesData?.data?.branches?.length > 0 ? (
                branchesData.data.branches.map((branch, index) => (
                  <tr
                    key={branch._id}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700 text-sm">
                      {(currentPage - 1) * limit + index + 1}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-900 capitalize text-sm">
                      {branch.name}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700 text-sm">
                      {branch.number}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700 text-sm">
                      {branch.receiverName}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700 text-sm">
                      {branch.receiverNumber}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700 text-sm">
                      {branch.address || "-"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700 text-sm">
                      {branch.branchCode || "-"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          branch.source === "courier"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {branch.source === "courier" ? "Courier" : "Manual"}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(branch)}
                          className="bg-blue-500 hover:bg-blue-600 text-white font-medium px-2.5 py-0.5 rounded text-xs transition-colors duration-200"
                        >
                          Edit
                        </button>

                        <button
                          onClick={() => handleDeleteBranchClick(branch._id)}
                          className="bg-red-500 hover:bg-red-600 text-white font-medium px-2.5 py-0.5 rounded text-xs transition-colors duration-200"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="9"
                    className="px-4 py-8 text-center text-sm text-gray-500"
                  >
                    No branches found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="flex justify-end items-center gap-4 py-5 pr-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || isLoading}
                className="px-4 py-2 rounded border text-base disabled:opacity-50 hover:bg-gray-50 transition-all duration-200"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-base">
                Page {currentPage} of{" "}
                {branchesData?.data?.pagination?.totalPages || 1}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={
                  currentPage ===
                    (branchesData?.data?.pagination?.totalPages || 1) ||
                  isLoading
                }
                className="px-4 py-2 rounded border text-base disabled:opacity-50 hover:bg-gray-50 transition-all duration-200"
              >
                Next
              </button>
            </div>
            <select
              className="ml-6 px-3 py-2 border rounded text-base min-w-[120px] z-10 bg-white"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
            >
              {limitOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt} / page
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Branch;
